// 20260405 -- Copilot (Claude Sonnet 4.6) -- Admin route guard for /admin/* routes
// WHY: Admin dashboard must be inaccessible to non-admin users without exposing a 403 or confirming the route exists

import { getInstance } from "./index";
import Logger from '@/utils/Logger'

const log = Logger.create('adminGuard', { showCaller: true, showLineNumbers: true })

/**
 * adminGuard - Vue Router navigation guard that enforces the stg-admin Auth0 role
 *
 * CREATED: 20260405
 * WHO: Copilot (Claude Sonnet 4.6)
 * WHY: Protect /admin/* routes from all users except those with the stg-admin Auth0 role.
 *      The role is injected into the ID token as a custom claim by an Auth0 Login Action.
 *
 * USAGE:
 * import { adminGuard } from '@/auth/adminGuard'
 * { path: '/admin/dashboard', component: AdminDashboard, beforeEnter: adminGuard }
 *
 * Auth0 Action injects roles at:
 *   Custom claim key: https://principaltask.com/roles (array of strings, e.g. ["stg-admin"])
 *   Tokens: both access token and ID token
 *
 * Role check strategy:
 *   Read directly from authService.user (set from the ID token on login). No forced token
 *   refresh — forcing cacheMode:'off' requires a refresh token in memory, which is not
 *   guaranteed (page reload, offline_access not issued, etc.) and caused a reauth loop.
 *   If the roles claim is present and includes stg-admin → grant.
 *   If the roles claim is present but excludes stg-admin → deny (confirmed non-admin).
 *   If the roles claim is absent → token may be stale; trigger loginWithRedirect to get
 *   fresh claims. TTL prevents an infinite loop if login repeatedly fails to produce claims.
 * 20260520 -- Copilot (Claude Sonnet 4.6) -- Rewrote to read from authService.user (no forced refresh)
 * WHY: getTokenSilently({cacheMode:'off'}) requires a refresh token; when unavailable it falls
 *      back to a blocked iframe, causing freshRoles=null and an unrecoverable reauth loop.
 *      authService.user holds the ID-token claims from the most recent valid login and is
 *      sufficient to check the stg-admin role without a network round-trip.
 */
export const adminGuard = (to, _from, next) => {
  const authService = getInstance();

  const REAUTH_TTL_MS = 5 * 60 * 1000;

  const checkRoles = () => {
    log.debug(`checkRoles: isAuthenticated=${authService.isAuthenticated}`)

    if (!authService.isAuthenticated) {
      // 20260520 -- Copilot (Claude Sonnet 4.6) -- Trigger login for unauthenticated users
      // WHY: Bouncing to /dashboard was wrong — the user should be asked to log in and
      //      then returned to the admin page. authGuard does this; adminGuard must too.
      log.warn('checkRoles: not authenticated — triggering loginWithRedirect')
      return authService.loginWithRedirect({ appState: { targetUrl: to.fullPath } });
    }

    const r = authService.user?.['https://principaltask.com/roles'];
    log.debug(`Roles claim from user: ${JSON.stringify(r)} (type: ${typeof r}, isArray: ${Array.isArray(r)})`)
    const roles = Array.isArray(r) ? r : null;
    log.debug(`roles resolved to: ${JSON.stringify(roles)}`)

    if (roles !== null && roles.includes('stg-admin')) {
      log.debug('Access GRANTED — stg-admin role confirmed from ID token')
      sessionStorage.removeItem('adminGuard_reauth_ts');
      return next();
    }

    if (roles !== null) {
      // Roles claim is present but stg-admin is not in it — confirmed non-admin.
      log.warn(`Access DENIED — roles claim present but stg-admin not included: ${JSON.stringify(roles)}`)
      return next({ path: '/dashboard' });
    }

    // roles is null: the ID token has no roles claim. This happens when the user's cached
    // token predates the Auth0 Action that injects roles, or when the claim was stripped by
    // a refresh that ran before the Action. Force a fresh login to obtain updated claims.
    const reauthTs = parseInt(sessionStorage.getItem('adminGuard_reauth_ts') ?? '0', 10) || 0;
    const timeSinceReauth = Date.now() - reauthTs;
    log.debug(`roles claim absent — reauth TTL check: timeSinceReauth=${timeSinceReauth}ms, TTL=${REAUTH_TTL_MS}ms`)

    if (timeSinceReauth >= REAUTH_TTL_MS) {
      log.debug('TTL expired — triggering loginWithRedirect for fresh token')
      sessionStorage.setItem('adminGuard_reauth_ts', String(Date.now()));
      return authService.loginWithRedirect({ appState: { targetUrl: to.fullPath } });
    }

    log.warn(`TTL active (${Math.round(timeSinceReauth / 1000)}s ago) — reauth already attempted, denying access`)
    return next({ path: '/dashboard' });
  };

  if (!authService.loading) {
    return checkRoles();
  }

  authService.$watch('loading', (loading) => {
    if (loading === false) {
      checkRoles();
    }
  });
};
